import { 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { auth } from '../firebase';
import { db, invitationsCollection } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { createConnection } from './connectionService';
import { getUserProfile } from './userService';

console.log('Debug - Database instance:', db);
console.log('Debug - Invitations collection reference:', invitationsCollection);

const invitationsRef = invitationsCollection;
const functions = getFunctions(undefined, 'us-central1');
const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');

export const sendInvitation = async (fromUser, toEmail) => {
  try {
    if (!auth.currentUser) {
      throw new Error('Must be authenticated to send invitations');
    }

    // Get user's profile to ensure we have the latest data
    const userProfile = await getUserProfile(fromUser.uid);
    if (!userProfile?.profileCompleted) {
      throw new Error('Please complete your profile before sending invitations');
    }

    // Get the ID token
    const idToken = await auth.currentUser.getIdToken();
    
    // Create the invitation document
    const invitation = {
      fromUserId: fromUser.uid,
      fromUserEmail: userProfile.notificationEmail || fromUser.email,
      fromUserName: userProfile.displayName || 'A Going Green Cards User',
      toEmail: toEmail,
      status: 'pending',
      createdAt: serverTimestamp(),
      recipientUserId: null,
      notificationEmail: userProfile.notificationEmail || fromUser.email
    };

    const docRef = await addDoc(invitationsCollection, invitation);
    console.log('Created invitation document:', docRef.id);

    // Call the HTTP function
    const response = await fetch(
      `https://us-central1-flashcards-web-3ba3c.cloudfunctions.net/sendInvitationEmailHttp`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ invitationId: docRef.id })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Cloud Function result:', result);

    return {
      id: docRef.id,
      ...invitation
    };
  } catch (error) {
    console.error('Detailed error in sendInvitation:', error);
    throw error;
  }
};

export const getReceivedInvitations = async (userId) => {
  try {
    console.log('Debug - Collection path:', invitationsRef.path);
    const currentUser = auth.currentUser;
    console.log('Debug - currentUser:', currentUser);
    console.log('Debug - userId:', userId);
    
    if (!currentUser) throw new Error('No authenticated user');

    console.log('Debug - Creating queries with email:', currentUser.email);
    
    const emailQuery = query(invitationsRef, 
      where('toEmail', '==', currentUser.email),
      where('status', '==', 'pending')
    );
    
    console.log('Debug - Email query created');
    
    const userIdQuery = query(invitationsRef,
      where('recipientUserId', '==', userId),
      where('status', '==', 'pending')
    );

    console.log('Debug - UserId query created');
    
    const [emailResults, userIdResults] = await Promise.all([
      getDocs(emailQuery),
      getDocs(userIdQuery)
    ]);
    
    console.log('Debug - Query results:', {
      emailResults: emailResults.docs.length,
      userIdResults: userIdResults.docs.length
    });

    const emailInvitations = emailResults.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const userIdInvitations = userIdResults.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Combine and deduplicate invitations
    const allInvitations = [...emailInvitations, ...userIdInvitations];
    const uniqueInvitations = Array.from(new Set(allInvitations.map(inv => inv.id)))
      .map(id => allInvitations.find(inv => inv.id === id));

    return uniqueInvitations;
  } catch (error) {
    console.error('Debug - Error in getReceivedInvitations:', error);
    throw error;
  }
};

export const getSentInvitations = async (userId) => {
  try {
    console.log('Debug - Getting sent invitations for userId:', userId);
    
    const sentQuery = query(invitationsRef, 
      where('fromUserId', '==', userId)
    );
    
    console.log('Debug - Sent query created');
    
    const snapshot = await getDocs(sentQuery);
    console.log('Debug - Sent query results:', snapshot.docs.length);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Debug - Error in getSentInvitations:', error);
    throw error;
  }
};

export const acceptInvitation = async (invitationId) => {
  try {
    if (!auth.currentUser) {
      throw new Error('Must be authenticated to accept invitations');
    }

    const invitationRef = doc(invitationsRef, invitationId);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = invitationDoc.data();
    
    // Create the connection regardless of email match
    await createConnection(invitation.fromUserId, auth.currentUser.uid);

    // Update invitation status with the authenticated user's info
    await updateDoc(invitationRef, {
      status: 'accepted',
      recipientUserId: auth.currentUser.uid,
      actualEmail: auth.currentUser.email, // Track which email actually accepted
      respondedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

export const declineInvitation = async (invitationId) => {
  try {
    const invitationRef = doc(invitationsRef, invitationId);
    await updateDoc(invitationRef, {
      status: 'declined',
      respondedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error declining invitation:', error);
    throw error;
  }
};

export const cancelInvitation = async (invitationId) => {
  try {
    const invitationRef = doc(invitationsRef, invitationId);
    await deleteDoc(invitationRef);
  } catch (error) {
    console.error('Error canceling invitation:', error);
    throw error;
  }
}; 